import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, MapPin, Search, Upload } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const SHOW_CAP = 500;

export default function StoresPage() {
  const { canEdit } = useAuth();
  const editable = canEdit("stores");
  const [stores, setStores] = useState([]);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState({ name: "", postcode: "", address: "" });
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const csvRef = useRef(null);

  const load = () => api.get("/stores").then((r) => setStores(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim() || !form.postcode.trim()) return toast.error("Store name and postcode are required");
    setSaving(true);
    try {
      await api.post("/stores", form);
      toast.success(`Store "${form.name}" added and geocoded`);
      setForm({ name: "", postcode: "", address: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const importCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    toast("Importing stores — this can take up to a minute for large files…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/stores/import", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      const bits = [`${data.imported} stores imported`];
      if (data.skipped_duplicates) bits.push(`${data.skipped_duplicates} duplicates skipped`);
      if (data.skipped_invalid) bits.push(`${data.skipped_invalid} rows skipped (no name / invalid UK postcode)`);
      if (data.not_geocoded) bits.push(`${data.not_geocoded} postcodes not found on the map`);
      toast.success(bits.join(" · "), { duration: 10000 });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setImporting(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete store "${s.name}"?`)) return;
    await api.delete(`/stores/${s.id}`);
    toast.success("Store deleted");
    load();
  };

  const filtered = stores.filter((s) => [s.name, s.postcode, s.address].join(" ").toLowerCase().includes(filter.toLowerCase()));
  const shown = filtered.slice(0, SHOW_CAP);

  return (
    <div className="p-8" data-testid="stores-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">STORES</h1>
          <p className="text-muted-foreground text-sm mt-1">{stores.length} delivery locations — postcodes are geocoded automatically</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="store-search-input" placeholder="Search name, postcode…" value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-none bg-card border-border pl-9 w-64 h-10" />
          </div>
          {editable && (
            <>
              <input ref={csvRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { importCsv(e.target.files[0]); e.target.value = ""; }} />
              <Button data-testid="import-stores-button" variant="outline" disabled={importing} onClick={() => csvRef.current?.click()} className="rounded-none border-border h-10 text-xs uppercase tracking-widest font-bold bg-transparent text-foreground hover:bg-muted hover:text-foreground">
                <Upload size={14} className="mr-1.5" /> {importing ? "Importing…" : "Import CSV"}
              </Button>
            </>
          )}
        </div>
      </div>

      {editable && (
      <div className="border border-border bg-card p-5 mb-6">
        <div className="uppercase text-xs tracking-[0.2em] font-bold text-muted-foreground mb-4">Add store</div>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-48">
            <Input data-testid="store-name-input" placeholder="Store name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="rounded-none bg-background border-border h-10" />
          </div>
          <div className="w-40">
            <Input data-testid="store-postcode-input" placeholder="Postcode" value={form.postcode} onChange={(e) => setForm((f) => ({ ...f, postcode: e.target.value.toUpperCase() }))} className="rounded-none bg-background border-border h-10 font-mono" />
          </div>
          <div className="flex-1 min-w-48">
            <Input data-testid="store-address-input" placeholder="Address (optional)" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} className="rounded-none bg-background border-border h-10" />
          </div>
          <Button data-testid="save-store-button" onClick={save} disabled={saving} className="rounded-none bg-[#FF3B30] hover:bg-[#ff554b] font-bold uppercase tracking-widest text-xs h-10">
            {saving ? "Geocoding…" : "Add store"}
          </Button>
        </div>
      </div>
      )}

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="stores-table">
          <thead>
            <tr className="text-left border-b border-border">
              {["Store", "Postcode", "Address", "Coordinates", ""].map((h, i) => (
                <th key={i} className="px-4 py-3 uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((s) => (
              <tr key={s.id} data-testid={`store-row-${s.name}`} className="border-b border-border/60 hover:bg-muted/60">
                <td className="px-4 py-3 font-semibold">{s.name}</td>
                <td className="px-4 py-3 font-mono text-foreground/80">{s.postcode}</td>
                <td className="px-4 py-3 text-muted-foreground">{s.address || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground font-mono text-xs">
                  {s.lat != null ? (
                    <span className="inline-flex items-center gap-1.5"><MapPin size={12} className="text-emerald-600 dark:text-[#32D74B]" /> {s.lat.toFixed(4)}, {s.lon.toFixed(4)}</span>
                  ) : (
                    <span className="text-amber-600 dark:text-[#FFD60A]">Postcode not found</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {editable && (
                  <button data-testid={`delete-store-${s.name}`} onClick={() => remove(s)} className="text-muted-foreground/70 hover:text-[#FF3B30] transition-colors duration-150"><Trash2 size={15} /></button>
                  )}
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-muted-foreground/70">{stores.length === 0 ? "No stores yet. Add stores with their postcode to plan routes." : "No stores match your search."}</td></tr>
            )}
          </tbody>
        </table>
        {filtered.length > SHOW_CAP && (
          <div className="px-4 py-3 text-xs text-muted-foreground border-t border-border" data-testid="stores-cap-note">
            Showing first {SHOW_CAP} of {filtered.length} — use the search box to narrow down.
          </div>
        )}
      </div>
    </div>
  );
}
